import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog } from "@threadws/jute";
import type { Persona } from "../../api/generated";
import {
  personasListQueryKey,
  personasRemoveMutation,
} from "../../api/generated/@tanstack/react-query.gen";

interface DeletePersonaDialogProps {
  /** The persona to delete. The dialog is open while this is set. */
  persona: Persona | null;
  onClose: () => void;
}

/** Asks before deleting a persona, then refreshes the list. */
export function DeletePersonaDialog({ persona, onClose }: DeletePersonaDialogProps) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    ...personasRemoveMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: personasListQueryKey() });
      onClose();
    },
  });

  // Closing can't cancel the request, so stay open until it settles.
  const close = () => {
    if (remove.isPending) return;
    remove.reset();
    onClose();
  };

  return (
    <Dialog
      open={persona !== null}
      onClose={close}
      title="Delete persona?"
      description={persona ? `"${persona.name}" will be removed.` : undefined}
      footer={
        <>
          <Button variant="ghost" icon="" disabled={remove.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            icon=""
            disabled={remove.isPending}
            onClick={() => persona && remove.mutate({ path: { id: persona.id } })}
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </Button>
        </>
      }
    >
      {remove.error && <p className="text-danger">{remove.error.message}</p>}
    </Dialog>
  );
}
